import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { useTranslation } from "react-i18next";

import unicsLogoImg from "images/GroupCompany/unics.png";
import iscLogoImg from "images/GroupCompany/isc.gif";

import BgTitle from "components/title/BgTitle";
import AffiliateCompanyMobileView from "./AffiliateCompanyMobileView";

const BgWrapper = tw.div`fancy-feature-mask`;

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;

const ThreeColumn = tw.div`flex flex-wrap`;
const Column = tw.div`xl:mr-12 xl:last:mr-0`;
const HeadingColumn = styled(Column)((props) => [
  tw`w-full xl:w-5/12`,
  props.textOnLeft ? tw`xl:order-first` : tw`xl:order-last xl:ml-12 xl:mr-0`,
]);
const CardColumn = tw(
  Column
)`w-full md:w-1/2 xl:w-3/12 mt-16 xl:mt-0 xl:last:ml-auto`;

const HeadingInfoContainer = tw.div`text-center xl:text-left max-w-lg xl:max-w-none mx-auto xl:mx-0`;
const HeadingDescription = tw.p`text-sm leading-relaxed mt-8`;

const Card = styled.div`
  ${tw`flex flex-col bg-gray-100 shadow-md rounded-lg p-6`};
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  }
`;

const CardImageContainer = tw.div`flex items-center justify-center h-20 bg-white rounded-lg p-4`;
const CardImage = styled.img`
  width: 66%; /* Explicit width to replace max-w-2/3 */
  height: auto;
  max-height: 100%;
  margin: auto;
`;

const CardText = tw.div`mt-4`;

const CardTitle = tw.h5`text-sm mt-4 font-bold`;

const CardMeta = styled.div`
  ${tw`flex flex-row flex-wrap  font-semibold tracking-wide text-gray-600 uppercase text-xs`}
`;

const CardMetaFeature = styled.div`
  ${tw`flex items-center mt-4 mr-4 last:mr-0`}
  svg {
    ${tw`w-5 h-5 mr-1`}
  }
`;

const Subtitle = tw.h6`text-sm text-gray-700 mt-2`;

const CardAction = tw(PrimaryButtonBase)`w-full mt-6 hover:bg-blue-700`;

const images = [unicsLogoImg, iscLogoImg];

export default ({ isXs = false }) => {
  if (isXs) return <AffiliateCompanyMobileView />;
  const { t } = useTranslation();

  const cards = t("affiliate_company_items", { returnObjects: true });

  return (
    <BgWrapper>
      <Container>
        <Content>
          <ThreeColumn>
            <HeadingColumn textOnLeft={false}>
              <HeadingInfoContainer>
                <BgTitle text={t("affiliate_company_headline")} />
                <HeadingDescription>
                  {t("affiliate_company_desc")}
                </HeadingDescription>
              </HeadingInfoContainer>
            </HeadingColumn>
            {cards.map((card, index) => (
              <CardColumn key={index}>
                <Card>
                  <CardImageContainer>
                    <CardImage src={images[index]} alt={card.company} />
                  </CardImageContainer>
                  <CardText>
                    <CardTitle>{card.title}</CardTitle>
                    <CardMeta>
                      <CardMetaFeature>
                        <LocationIcon /> &nbsp;{card.address}
                      </CardMetaFeature>
                    </CardMeta>

                    <Subtitle>
                      <i class="fa-solid fa-phone"></i> &nbsp;{card.phone}
                    </Subtitle>

                    <Subtitle>
                      <i class="fa-solid fa-envelope"></i> &nbsp;{card.mail}
                    </Subtitle>

                    <a
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardAction>
                        <i
                          className="fa-solid fa-globe"
                          style={{ fontSize: "1.5rem" }}
                        ></i>
                      </CardAction>
                    </a>
                  </CardText>
                </Card>
              </CardColumn>
            ))}
          </ThreeColumn>
        </Content>
      </Container>
    </BgWrapper>
  );
};
